import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Form, Modal, OverlayTrigger, Popover, Spinner, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import moment from 'moment';

import { brandSelectTheme } from '../Common/reactSelectTheme';
import { resolveRoleAccounts } from '../../../services/api/chartRolesApi';

import {
    Landmark,
    RefreshCw,
    Users,
    CheckCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    AlertTriangle,
    Info,
    MinusCircle,
    Sparkles,
    TrendingUp,
    TrendingDown,
    FilePlus,
    FileText,
    SlidersHorizontal,
    Undo2,
    Upload,
    X as XIcon,
} from 'lucide-react';

import PageHeader from '../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../Common/ListToolbar';
import { EmptyState } from '../Common/EmptyState';
import { useListControls } from '../../../hooks/useListControls';
import BankImportModal from './BankImportModal';
import CreateVerificationFromTransactionModal from './CreateVerificationFromTransactionModal';
import VerificationDetailsModal from '../Accounting/Verification/VerificationDetailsModal';
import {
    listBankStatements,
    bulkMarkReconciled,
    extractListTotal,
    getUnifiedSuggestions,
    confirmSuggestion,
    resetStatement,
    submitMatchingFeedback,
    getStatementVerification,
    getBankAccountMappings,
    getVerificationById,
    getLlmSuggestion,
    runAutoMatchAll,
    getAttentionCounts,
    linkSafeMatches,
    bulkUnmatchStatements,
    bookSimilar,
    getSimilarCount,
    restoreRejectedSuggestions,
    buildConfirmTargets,
    buildHistoryPrefillRows,
    hasStrongSuggestion,
    isTransferSuggestion,
    llmDenialKind,
    normalizeSuggestionRows,
    strategyLabel,
    confidenceTier,
    priorsEvidenceText,
    deriveDisplayState,
    sortRowsByAccount,
    ATTENTION_STATES,
    DISPLAY_BOOKED,
    DISPLAY_TO_CONFIRM,
    DISPLAY_SUGGESTED,
    DISPLAY_NEEDS_YOU,
    DISPLAY_CLEARED,
    DISPLAY_SKIPPED,
    TIER_MATCH,
    TIER_REVIEW,
    TIER_DONE,
} from '../../../services/MatchingService';
import {
    getRuleProposals,
    dismissRuleProposal,
    findMatchingProposal,
    proposalToFormRule,
} from '../../../services/InsightsService';
import { useFeatures } from '../../../context/FeatureContext';
import {
    bankTransactionDescriptor,
    reconciliationQueueItemDescriptor,
    handOffState,
    useSurfaceTaskDescriptor,
} from '../../../context/TaskContextDescriptor';

// The server caps unpaginated list calls (default 50) — fetch a big page
// explicitly so the queue sees the whole backlog. Real paging is a later
// iteration; until then we tell the user when the backlog is even bigger
// than this window.
const QUEUE_FETCH_LIMIT = 500;

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Bankhändelser — THE bank-transaction home (UX redesign Phase C,
 * docs/TRANSACTION_MATCHING_UX_REDESIGN.md §4.3). One page merges the old
 * Matchning queue and the Banktransaktioner ledger:
 *
 *   - Default filter "Att hantera": the exception inbox — rows that need
 *     the user (att bekräfta / förslag finns / behöver dig).
 *   - "Alla": the full ledger view with server-side paging ("Ladda fler").
 *   - "Klara": the done pile (bokförd / avstämd / överhoppad).
 *
 * Every row carries a categorical display_state badge (§4.2, derived
 * server-side from status + the persisted suggestion snapshot — never a
 * raw percentage). Import is the ONLY manual step: the backend auto-runs
 * the matching engine on import (B3), and this page silently runs it once
 * when it finds rows the engine never saw. Undo lives on the row.
 */

// Date-window presets (owner 2026-07-27): coarse noise filter over the
// whole page — pills, counts and batch actions all follow the window.
const DATE_RANGE_OPTIONS = [
    { value: 'all', label: 'Alla datum' },
    { value: 'm1', label: 'Senaste månaden' },
    { value: 'm3', label: 'Senaste 3 mån' },
    { value: 'year', label: 'I år' },
    { value: 'custom', label: 'Anpassat…' },
];

const FILTER_ATTENTION = 'attention';
const FILTER_MATCHED = 'matched';
const FILTER_ALL = 'all';
const FILTER_DONE = 'done';
const FILTERS = [
    { key: FILTER_ATTENTION, label: 'Att hantera' },
    { key: FILTER_MATCHED, label: 'Att koppla' },
    { key: FILTER_ALL, label: 'Alla' },
    { key: FILTER_DONE, label: 'Klara' },
];

// Optimistic overlay over the server-side rejection suppression (Phase B):
// the server filters rejected suggestions on every fetch; this set hides
// one the instant the user rejects it, before the refetch lands.
const rejectionKey = (lineId, s) =>
    `${lineId}::${s?.candidateId || s?.matchStrategy || s?.strategy || 'x'}`;

// The five categorical user-facing states (§4.2) — colors get tokenized in
// the Phase E visual pass; labels are the contract.
const DISPLAY_CONFIG = {
    [DISPLAY_BOOKED]: { label: 'Bokförd', color: '#10b981', icon: CheckCircle },
    [DISPLAY_TO_CONFIRM]: { label: 'Att bekräfta', color: '#b45309', icon: CheckCircle },
    [DISPLAY_SUGGESTED]: { label: 'Förslag finns', color: 'var(--primary)', icon: Sparkles },
    [DISPLAY_NEEDS_YOU]: { label: 'Behöver dig', color: '#dc2626', icon: AlertCircle },
    [DISPLAY_CLEARED]: { label: 'Avstämd', color: '#6b7280', icon: CheckCircle2 },
    [DISPLAY_SKIPPED]: { label: 'Överhoppad', color: '#9ca3af', icon: MinusCircle },
};

// Statuses the batch action confirms, and statuses "Ångra" can reset.
const BATCH_CONFIRM_STATUSES = ['auto_matched', 'manual_matched'];
const RESETTABLE_STATUSES = ['auto_matched', 'manual_matched', 'reconciled_external', 'skipped'];

// The batch target set ("Granska och godkänn"): rows the system is sure
// about. Three families, one review surface — the row's own label says
// what happens to it (Kopplad/Koppla/Förslag vocabulary).
const isBatchConfirmable = (l) => BATCH_CONFIRM_STATUSES.includes(l.reconciliation_status);
const isLinkSafe = (l) => l.reconciliation_status === 'unmatched'
    && ['verification', 'reference_exact', 'reference_customer'].includes(l.suggestion_snapshot?.strategy)
    && l.suggestion_snapshot?.tier === 'high';

// List ordering (Inkorg parity). 'Behöver dig först' surfaces the rows the
// system is explicitly asking about, then engine matches to confirm, then
// suggestions.
const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Senaste först' },
    { value: 'date_asc', label: 'Äldst först' },
    { value: 'amount_desc', label: 'Största belopp' },
    { value: 'amount_asc', label: 'Minsta belopp' },
    { value: 'state', label: 'Behöver dig först' },
];
const STATE_ORDER = {
    [DISPLAY_NEEDS_YOU]: 0,
    [DISPLAY_TO_CONFIRM]: 1,
    [DISPLAY_SUGGESTED]: 2,
    [DISPLAY_BOOKED]: 3,
    [DISPLAY_CLEARED]: 4,
    [DISPLAY_SKIPPED]: 5,
};

// Detail-pane mode for a display state: to_confirm behaves like the old
// Matchade pane, the other attention states like Att granska, done like
// Klara.
function paneModeFor(displayState) {
    if (displayState === DISPLAY_TO_CONFIRM) return TIER_MATCH;
    if (ATTENTION_STATES.includes(displayState)) return TIER_REVIEW;
    return TIER_DONE;
}

function StatusBadge({ line }) {
    const cfg = DISPLAY_CONFIG[deriveDisplayState(line)] || DISPLAY_CONFIG[DISPLAY_NEEDS_YOU];
    const Icon = cfg.icon;
    return (
        <span style={{ color: cfg.color, fontWeight: 500, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <Icon size={13} />
            {cfg.label}
        </span>
    );
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
}

function formatDate(value) {
    if (!value) return '-';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : String(value);
}

// Parsed suggestion snapshot for a list row (string on the mysql driver,
// object on newer payloads). null when absent/broken.
function snapshotOf(line) {
    const raw = line?.suggestion_snapshot;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch (e) { return null; }
}

// Categorical certainty pill for the list (§4.3 — tier, never a raw score).
const ROW_TIER = {
    high: { label: 'Hög', color: '#10b981', bg: '#ecfdf5' },
    medium: { label: 'Medel', color: '#b45309', bg: '#fffbeb' },
    low: { label: 'Låg', color: '#6b7280', bg: '#f3f4f6' },
};

/**
 * The row's match line — WHAT the engine knows about this row, shown in
 * the list itself instead of only in the detail pane (owner feedback
 * 2026-07-26): the snapshot label for suggestions ("Bokförd som V99 …",
 * "Regel: …"), the match target for engine matches, and an honest
 * "no suggestion" for the rest.
 */
function rowMatchInfo(line) {
    const state = deriveDisplayState(line);
    const snap = snapshotOf(line);
    switch (state) {
        case DISPLAY_SUGGESTED:
            return {
                text: (snap && snap.label) || strategyLabel(snap && snap.strategy) || 'Förslag finns',
                tier: snap && snap.tier,
                // Hover-preview payload (owner feedback 2026-07-26): the
                // provenance sentence + the proposed kontering rows.
                detail: (snap && snap.detail) || null,
                rows: (snap && Array.isArray(snap.rows)) ? snap.rows : [],
            };
        case DISPLAY_TO_CONFIRM:
            // Same verb family as the suggestions, tense carries the state:
            // 'Koppla till …' = proposal (nothing linked yet);
            // 'Kopplad till …' = the engine already made the link, the
            // click only confirms it.
            return {
                text: line.verification_id
                    ? `Kopplad till verifikat${line.verification_number ? ` ${line.verification_series || ''}${line.verification_number}` : ''}`
                    : (line.matched_invoice_id ? 'Kopplad till faktura' : 'Matchad automatiskt'),
            };
        case DISPLAY_NEEDS_YOU:
            return { text: 'Inget förslag — öppna för att bokföra' };
        case DISPLAY_BOOKED:
            return {
                text: line.verification_id
                    ? `Verifikat ${line.verification_series || ''}${line.verification_number || ''} kopplat`.replace('  ', ' ')
                    : 'Bekräftad som avstämd',
                // No human matcher on a booked row = the system did it
                // (rule auto-post) — say so.
                auto: Boolean(line.verification_id && !line.matched_by_user_id),
            };
        default:
            return null;
    }
}

// One statement line as a compact two-line row: transaction facts on top,
// the match story + status underneath. Hovering a suggested row opens a
// kontering preview popover — see the proposed booking without clicking
// (owner feedback 2026-07-26).
function StatementRow({ line, bankLeg, isSelected, isPending, disablePreview, onSelect }) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const match = rowMatchInfo(line);
    // Hover preview shows the COMPLETE verifikat: the bank leg (mapped
    // konto, exact line amount) + the proposed counter rows.
    const previewRows = (match && match.rows && match.rows.length > 0 && bankLeg)
        ? [{
            account_number: bankLeg.account_number,
            account_name: bankLeg.account_name,
            debit: isInflow ? Math.abs(amount) : 0,
            credit: isInflow ? 0 : Math.abs(amount),
        }, ...match.rows]
        : (match ? match.rows : []);
    const tier = match && match.tier ? ROW_TIER[match.tier] : null;
    const hasPreview = Boolean(match && (match.detail || (match.rows && match.rows.length > 0)));

    const row = (
        <button
            type="button"
            onClick={onSelect}
            className="matching-list-item"
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                background: isSelected ? 'var(--rgba-primary-1)' : 'transparent',
                padding: '10px 16px',
                cursor: 'pointer',
                // Loading feedback (owner feedback 2026-07-26): the clicked
                // row dims while its suggestions load — unmissable next to
                // the spinner.
                opacity: isPending ? 0.5 : 1,
                transition: 'opacity 0.15s ease',
            }}
            aria-busy={isPending || undefined}
        >
            {/* Row 1 — date + description + amount */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isInflow
                    ? <TrendingUp size={14} style={{ color: 'var(--bs-success)', flexShrink: 0 }} />
                    : <TrendingDown size={14} style={{ color: 'var(--bs-danger)', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap' }}>
                    {formatDate(line.transaction_date)}
                </span>
                <span style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {line.description || '(ingen beskrivning)'}
                </span>
                <span style={{
                    marginLeft: 'auto',
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: isInflow ? 'var(--bs-success)' : 'var(--bs-danger)',
                }}>
                    {formatCurrency(amount)}
                </span>
            </div>
            {/* Row 2 — the match story + tier + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                {match && (
                    <span style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.text}
                    </span>
                )}
                {match && match.auto && (
                    <span style={{
                        flexShrink: 0,
                        border: '1px solid #e5e7eb',
                        color: '#6b7280',
                        borderRadius: 999,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: '18px',
                    }}
                    title="Bokförd automatiskt av en regel"
                    >
                        Auto
                    </span>
                )}
                {tier && (
                    <span style={{
                        flexShrink: 0,
                        background: tier.bg,
                        color: tier.color,
                        borderRadius: 999,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: '18px',
                    }}>
                        {tier.label}
                    </span>
                )}
                <span style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {isPending && <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: 'var(--primary)' }} />}
                    <StatusBadge line={line} />
                </span>
            </div>
        </button>
    );

    // No hover preview while the detail panel is open (the full answer is
    // already on screen) or while this row is loading.
    if (!hasPreview || disablePreview || isPending) return row;

    // Kontering preview on hover: the proposed booking + provenance,
    // without opening the detail pane.
    return (
        <OverlayTrigger
            placement="auto"
            delay={{ show: 350, hide: 0 }}
            overlay={(
                <Popover id={`row-preview-${line.id}`} className="bankrow-popover" style={{ maxWidth: 340 }}>
                    <Popover.Body>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1f2937' }}>
                            {match.text}
                        </div>
                        {match.detail && match.detail !== match.text && (
                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                                {match.detail}
                            </div>
                        )}
                        {previewRows && previewRows.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                {sortRowsByAccount(previewRows).map((r, i) => (
                                    <div
                                        key={`${r.account_number}-${i}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 8,
                                            padding: '4px 0',
                                            borderTop: i > 0 ? '1px dashed #e5e7eb' : 'none',
                                        }}
                                    >
                                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                                            {r.account_number}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.account_name || ''}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#374151', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                            {r.debit > 0 ? `D ${formatCurrency(r.debit)}` : `K ${formatCurrency(r.credit)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                            Klicka på raden för att bekräfta eller ändra.
                        </div>
                    </Popover.Body>
                </Popover>
            )}
        >
            {row}
        </OverlayTrigger>
    );
}

/**
 * Per-strategy details block for a suggestion — structured Swedish
 * fields, never the backend's English matchReason (audit A3/B8).
 */
function suggestionDetails(suggestion) {
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    const rows = [];
    const push = (label, value) => {
        if (value != null && value !== '') rows.push([label, value]);
    };
    const money = (value) => (
        value != null && value !== '' && Number.isFinite(parseFloat(value))
            ? formatCurrency(parseFloat(value))
            : null
    );

    switch (strategy) {
        case 'verification':
            push('Verifikat', suggestion.verificationNumber);
            push('Datum', formatDate(suggestion.verificationDate || suggestion.date));
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
        case 'reference_exact':
        case 'reference_customer':
            push('Faktura', Array.isArray(suggestion.invoiceNumbers)
                ? suggestion.invoiceNumbers.join(' + ')
                : suggestion.invoiceNumber);
            push('Kund', suggestion.customerName);
            push('Belopp', money(suggestion.amount));
            push('Fakturadatum', suggestion.invoiceDate ? formatDate(suggestion.invoiceDate) : null);
            break;
        case 'origination_supplier_payment':
            push('Faktura', suggestion.invoiceNumber);
            push('Leverantör', suggestion.supplierName);
            push('Belopp', money(suggestion.amount));
            push('Betaldatum', suggestion.paymentDate ? formatDate(suggestion.paymentDate) : null);
            break;
        case 'origination_payroll':
            push('Utbetalningsdatum', suggestion.payDate ? formatDate(suggestion.payDate) : null);
            push('Belopp', money(suggestion.amount));
            break;
        case 'origination_tax_transfer':
            push('Skattekontorad', formatDate(suggestion.taxStatementDate));
            push('Belopp', money(suggestion.amount));
            break;
        case 'internal_transfer':
            push('Motkonto', suggestion.accountNumber);
            push('Datum', formatDate(suggestion.transactionDate));
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
        default:
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
    }
    return rows;
}

// Class-plausibility (and other) soft warnings — one amber line each,
// on any suggestion card that carries them. Guidance, never a block.
function SuggestionWarnings({ warnings }) {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    return (
        <div className="mt-2">
            {warnings.map((warning) => (
                <div key={warning} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', color: '#b45309', fontSize: '0.75rem', marginTop: 2 }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>{warning}</span>
                </div>
            ))}
        </div>
    );
}

// Small uppercase section marker — the state of the row in one word,
// before any content. The eyebrow + verdict sentence replace the old
// "Matchningsförslag" jargon header (owner feedback 2026-07-26: the pane
// must be self-explanatory, never explained).
function Eyebrow({ color = 'var(--primary)', children }) {
    return (
        <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color,
            marginBottom: 4,
        }}>
            {children}
        </div>
    );
}

// The kontering drawn as the verifikat slip it will become — account bold,
// name muted, belopp right, dashed receipt separators. This is the hero of
// a booking proposal: the user reads the outcome, not a data table.
function KonteringSlip({ rows }) {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', marginTop: 10 }}>
            {sortRowsByAccount(rows).map((row, index) => {
                const debit = parseFloat(row.debit) || 0;
                const credit = parseFloat(row.credit) || 0;
                return (
                    <div
                        key={`${row.account_number}-${index}`}
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 10,
                            padding: '9px 14px',
                            borderTop: index > 0 ? '1px dashed #e5e7eb' : 'none',
                        }}
                    >
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                            {row.account_number}
                        </span>
                        <span style={{ fontSize: 12.5, color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.account_name || ''}
                        </span>
                        <span style={{ fontSize: 12.5, color: '#374151', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {debit > 0 ? `Debet ${formatCurrency(debit)}` : `Kredit ${formatCurrency(credit)}`}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// Retrieval-source labels for history suggestions (L3v2 blend). Older
// payloads omit retrievalSource entirely → no chip. Purely informational.
const RETRIEVAL_SOURCE_LABELS = {
    embedding: 'AI-minne',
    lexical: 'Textmatch',
    both: 'AI-minne + text',
};

// Small muted chip telling the user HOW a history precedent was found —
// only rendered when retrievalSource is present on the suggestion.
function RetrievalSourceChip({ source }) {
    const label = RETRIEVAL_SOURCE_LABELS[source];
    if (!label) return null;
    return (
        <span
            title="Hur det här förslaget hittades"
            style={{
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                color: '#6b7280',
                borderRadius: 999,
                padding: '1px 8px',
                fontSize: '0.6875rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </span>
    );
}

// Neighbor chips ("V12 · 2026-03-04") — each opens the precedent
// verifikat in the shared VerificationDetailsModal.
function NeighborChips({ neighbors, busy, onOpen }) {
    if (!Array.isArray(neighbors) || neighbors.length === 0) return null;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {neighbors.map((neighbor) => (
                <button
                    key={neighbor.id}
                    type="button"
                    disabled={busy}
                    onClick={() => onOpen(neighbor)}
                    title={neighbor.description || ''}
                    style={{
                        border: '1px solid #d1d5db',
                        background: '#f9fafb',
                        color: '#374151',
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    <FileText size={11} />
                    {neighbor.number}{neighbor.date ? ` · ${formatDate(neighbor.date)}` : ''}
                </button>
            ))}
        </div>
    );
}

/**
 * History-layer suggestion card (§4.1 L3 — "bokförd som V-… tidigare").
 *
 * Two shapes:
 *  - precedent template: matchReason headline + neighbor chips + the
 *    scaled counter rows; "Bokför så här" prefills the booking wizard
 *    (an L3 suggestion proposes a BOOKING, not a match — never the
 *    confirm-suggestion endpoint).
 *  - group backoff (groupSuggestion): info-style guidance card only —
 *    kontogrupp hint, no rows, no confirm button.
 */
function HistorySuggestionCard({ suggestion, bankLeg, busy, onPrefillBooking, onOpenNeighbor, onReject, guidanceOnly }) {
    const tier = confidenceTier(suggestion.confidence);

    if (suggestion.groupSuggestion) {
        // Guidance card — neutral gray like the priors evidence line (Phase E:
        // the old blue info box was off-palette; guidance is muted, not blue).
        return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 8, background: '#f9fafb' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Info size={16} style={{ color: '#6b7280', flexShrink: 0, marginTop: 1 }} />
                    <div>
                        <div style={{ fontSize: '0.8125rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>
                                {suggestion.matchReason
                                    || `Liknande bokförs oftast i grupp ${suggestion.group}${suggestion.groupName ? ` – ${suggestion.groupName}` : ''}`}
                            </span>
                            <RetrievalSourceChip source={suggestion.retrievalSource} />
                        </div>
                        {suggestion.usageCount > 0 && (
                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                                Baserat på {suggestion.usageCount} liknande bokningar — vägledning, inget förslag att bekräfta.
                            </div>
                        )}
                        <NeighborChips neighbors={suggestion.neighborVerifications} busy={busy} onOpen={onOpenNeighbor} />
                    </div>
                </div>
                <SuggestionWarnings warnings={suggestion.warnings} />
            </div>
        );
    }

    const usage = suggestion.usageCount || 0;
    const top = (suggestion.neighborVerifications || [])[0];

    return (
        <div style={{ marginBottom: 16 }}>
            <Eyebrow>Förslag</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    Bokför så här — så bokförde du liknande transaktioner tidigare.
                </span>
                <Badge bg={tier.variant} style={{ fontSize: '0.6875rem' }}>
                    {tier.label}
                </Badge>
            </div>

            {/* The COMPLETE verifikat incl. the bank leg from the account
                mapping (owner feedback 2026-07-26) — the slip shows exactly
                what booking will create, balanced. */}
            <KonteringSlip rows={
                buildHistoryPrefillRows(suggestion, bankLeg)
                || normalizeSuggestionRows(suggestion.rows)
            } />

            {/* Provenance in small print — evidence, not headline. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>
                    {usage > 0
                        ? `Baserat på ${usage} tidigare ${usage === 1 ? 'bokföring' : 'bokföringar'}${top ? `, senast ${top.number || formatDate(top.date)}` : ''}`
                        : 'Baserat på din bokföringshistorik'}
                </span>
                <RetrievalSourceChip source={suggestion.retrievalSource} />
            </div>
            <NeighborChips neighbors={suggestion.neighborVerifications} busy={busy} onOpen={onOpenNeighbor} />
            <SuggestionWarnings warnings={suggestion.warnings} />

            {guidanceOnly ? (
                // A booked verifikat already matches this line — booking again
                // would duplicate it. Show the history purely as context
                // ("so this is booked", above) with no booking action.
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                    Så här brukar du bokföra liknande — koppla till det bokförda verifikatet ovan i stället för att bokföra på nytt.
                </p>
            ) : (
                <div className="d-flex gap-2 mt-2">
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => onPrefillBooking(suggestion)}>
                        <FilePlus size={14} className="me-1" />
                        Bokför så här
                    </Button>
                    <Button size="sm" variant="link" className="text-muted p-1" disabled={busy} onClick={() => onReject(suggestion)}>
                        <XIcon size={14} className="me-1" />
                        Avvisa
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * AI-förslag card — the entitlement-gated LLM fallback. The LLM proposes
 * a BOOKING (rows + Swedish rationale), so its confirm prefills the
 * booking wizard for review — never the confirm-suggestion endpoint.
 * Amber review styling: an AI answer is always granska-först.
 */
function LlmSuggestionCard({ suggestion, busy, onUse }) {
    const rows = normalizeSuggestionRows(suggestion.rows);
    const rationale = suggestion.rationale || suggestion.motivation || suggestion.reasoning || suggestion.matchReason;
    return (
        <div style={{ marginBottom: 16 }}>
            <Eyebrow color="#b45309">
                <Sparkles size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                AI-förslag — granska innan du bokför
            </Eyebrow>
            {rationale && (
                <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: 0 }}>
                    {rationale}
                </p>
            )}
            <KonteringSlip rows={rows} />
            <SuggestionWarnings warnings={suggestion.warnings} />
            <div className="d-flex gap-2 mt-2">
                <Button size="sm" variant="warning" disabled={busy || rows.length < 2} onClick={() => onUse(suggestion)}>
                    <FilePlus size={14} className="me-1" />
                    Använd förslaget
                </Button>
            </div>
        </div>
    );
}

/**
 * Plain-language verdict per strategy (owner feedback 2026-07-26): the
 * pane states the situation in one sentence a first-time user parses
 * without help. 'Koppla' verdicts always carry the no-double-booking
 * reassurance — the exact fear the Bokio anti-pattern taught us to
 * pre-empt.
 */
function verdictFor(suggestion) {
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    switch (strategy) {
        case 'verification':
            return {
                eyebrow: 'Redan bokförd?',
                sentence: `Raden ser ut att motsvara ${suggestion.verificationNumber ? `verifikat ${suggestion.verificationNumber}` : 'ett verifikat'} som redan finns i din bokföring.`,
                action: 'Koppla till verifikatet',
                note: 'Inget nytt bokförs — raden kopplas ihop med det befintliga verifikatet.',
            };
        case 'reference_exact':
        case 'reference_customer': {
            const invoice = Array.isArray(suggestion.invoiceNumbers)
                ? suggestion.invoiceNumbers.join(' + ')
                : suggestion.invoiceNumber;
            return {
                eyebrow: 'Fakturabetalning',
                sentence: `Raden ser ut som betalningen av ${invoice ? `faktura ${invoice}` : 'en kundfaktura'}.`,
                action: 'Koppla till fakturan',
                note: 'Fakturan markeras som betald och raden kopplas till den.',
            };
        }
        case 'origination_payroll':
            return {
                eyebrow: 'Egen utbetalning',
                sentence: 'Raden matchar en lönekörning du gjort i LedgerLoop.',
                action: 'Koppla till lönekörningen',
                note: 'Inget nytt bokförs — raden kopplas till lönekörningen.',
            };
        case 'origination_supplier_payment':
            return {
                eyebrow: 'Egen utbetalning',
                sentence: 'Raden matchar en leverantörsbetalning du registrerat.',
                action: 'Koppla till betalningen',
                note: 'Inget nytt bokförs — raden kopplas till betalningen.',
            };
        case 'origination_tax_transfer':
            return {
                eyebrow: 'Skattekonto',
                sentence: 'Raden ser ut som en överföring till ditt skattekonto.',
                action: 'Bekräfta',
                note: null,
            };
        case 'internal_transfer':
            return {
                eyebrow: 'Egen överföring',
                sentence: 'Raden ser ut som en överföring mellan dina egna konton.',
                action: 'Bokför överföring',
                note: null,
            };
        case 'rule':
            return {
                eyebrow: 'Regel',
                sentence: suggestion.ruleName
                    ? `Regeln "${suggestion.ruleName}" känner igen den här transaktionen.`
                    : 'En av dina regler känner igen den här transaktionen.',
                action: 'Bokför enligt regeln',
                note: null,
            };
        default:
            return {
                eyebrow: 'Förslag',
                sentence: strategyLabel(strategy),
                action: 'Bekräfta',
                note: null,
            };
    }
}

// Deterministic-match card: verdict sentence first, a compact fact line,
// ONE primary action. No jargon headers, no data tables.
function InlineSuggestionCard({ suggestion, busy, onConfirm, onBookTransfer, onReject, guidanceOnly, bankLeg, companyId }) {
    const tier = confidenceTier(suggestion.confidence);
    const transfer = isTransferSuggestion(suggestion);
    const confirmTargets = buildConfirmTargets(suggestion);
    const confirmable = confirmTargets.length > 0;
    const verdict = verdictFor(suggestion);
    const details = suggestionDetails(suggestion);
    // One-step (owner follow-up 2026-07-26): confirming an invoice match
    // also registers the payment — one action, plain sentence, with
    // "endast koppla" as the escape hatch.
    const invoiceMatch = confirmable
        && confirmTargets.every((t) => t.target_type === 'customer_invoice');

    // "Redan bokförd?" answers itself (owner 2026-07-27): verifikat
    // proposals load the target verifikat and show its kontering inline.
    const [targetVerification, setTargetVerification] = useState(null);
    const strategyKey = suggestion.matchStrategy || suggestion.strategy;
    useEffect(() => {
        setTargetVerification(null);
        if (strategyKey !== 'verification' || !suggestion.candidateId) return undefined;
        let cancelled = false;
        getVerificationById(companyId, suggestion.candidateId)
            .then((data) => {
                if (!cancelled) setTargetVerification(data?.verification || data || null);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [strategyKey, suggestion.candidateId, companyId]);
    // A booked verifikat already matches this line — a transfer that BOOKS a
    // new verifikat would duplicate it (walkthrough finding 2026-07-20).
    // Confirm-type suggestions (link only) are always safe.
    const suppressTransferBooking = transfer && guidanceOnly;

    return (
        <div style={{ marginBottom: 16 }}>
            <Eyebrow>{verdict.eyebrow}</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    {verdict.sentence}
                </span>
                <Badge bg={tier.variant} style={{ fontSize: '0.6875rem' }}>
                    {tier.label}
                </Badge>
            </div>

            {details.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 6 }}>
                    {details.map(([label, value]) => `${label}: ${value}`).join(' · ')}
                </div>
            )}

            <SuggestionWarnings warnings={suggestion.warnings} />

            {targetVerification && (
                <KonteringSlip rows={(targetVerification.transactions || []).map((t) => ({
                    account_number: t.account_number,
                    account_name: t.account_name,
                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                }))} />
            )}

            {suppressTransferBooking ? (
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                    Ett bokfört verifikat matchar redan raden (ovan) — koppla till det i stället för att bokföra överföringen på nytt.
                </p>
            ) : (
                <>
                    <div className="d-flex gap-2 mt-2">
                        {transfer ? (
                            <Button size="sm" variant="primary" disabled={busy} onClick={() => onBookTransfer(suggestion)}>
                                <FilePlus size={14} className="me-1" />
                                {verdict.action}
                            </Button>
                        ) : invoiceMatch ? (
                            <>
                                <Button size="sm" variant="primary" disabled={busy} onClick={() => onConfirm(suggestion, { registerPayment: true })}>
                                    <CheckCircle size={14} className="me-1" />
                                    Koppla och registrera betalning
                                </Button>
                                <Button size="sm" variant="link" className="text-muted p-1" disabled={busy} onClick={() => onConfirm(suggestion)}>
                                    Endast koppla
                                </Button>
                            </>
                        ) : (
                            <Button size="sm" variant="primary" disabled={busy || !confirmable} onClick={() => onConfirm(suggestion)}>
                                <CheckCircle size={14} className="me-1" />
                                {verdict.action}
                            </Button>
                        )}
                        <Button size="sm" variant="link" className="text-muted p-1" disabled={busy} onClick={() => onReject(suggestion)}>
                            <XIcon size={14} className="me-1" />
                            Avvisa
                        </Button>
                    </div>
                    {invoiceMatch ? (
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                            Fakturan markeras som betald och betalningen bokförs på
                            {' '}{(bankLeg && bankLeg.account_number) || 'företagskontot (rollen är inte mappad)'} med radens datum.
                        </div>
                    ) : verdict.note ? (
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                            {verdict.note}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

/**
 * Global-priors evidence line (§4.6b, Phase 4 item 3) — the cross-tenant
 * "{percent} av N företag bokför liknande som …" advisory. Rendered ONLY when
 * the line has no strong own suggestion. It is EVIDENCE, never authority: a
 * muted informational line, never a confirm button, never overriding a
 * suggestion. Renders nothing when there is no safe prior.
 */
function PriorsEvidenceLine({ prior }) {
    const text = priorsEvidenceText(prior);
    if (!text) return null;
    return (
        <div
            style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 8,
                padding: '10px 12px', margin: '12px 0',
            }}
        >
            <Users size={14} style={{ color: '#6b7280', flexShrink: 0, marginTop: 2 }} />
            <div>
                <div style={{ fontSize: '0.8125rem', color: '#4b5563' }}>{text}</div>
                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                    Vägledning från andra företag — inget att bekräfta, dina egna regler och tidigare bokföringar går alltid först.
                </div>
            </div>
        </div>
    );
}

// Right-pane detail for the selected line, tier-aware (audit D5/A7):
// Att granska gets inline suggestions + booking; Matchade gets
// bekräfta/ångra; Klara is read-only with Visa verifikat + Ångra.
function StatementDetail({
    line,
    bankLeg,
    companyId,
    tier,
    onClose,
    suggestions,
    suggestionsLoading,
    suggestionsError,
    actionBusy,
    onConfirmSuggestion,
    onBookTransfer,
    onRejectSuggestion,
    onRestoreRejections,
    rejectedCount,
    onPrefillHistoryBooking,
    onOpenNeighbor,
    prior,
    llm,
    onAskAi,
    onUseLlmSuggestion,
    onCreateVerification,
    onMarkReconciledExternal,
    onConfirmSingle,
    onReset,
    onShowVerification,
    linkedVerification,
    onAskAssistant,
}) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const status = line.reconciliation_status;
    // "Ångra" is legal for the four resettable statuses (D4/A7) — plus
    // batch-confirmed lines (status verified WITHOUT a verifikat), so the
    // batch dialog's undo promise stays true. Lines with a real verifikat
    // are never resettable from here.
    const canReset = RESETTABLE_STATUSES.includes(status)
        || (status === 'verified' && !line.verification_id);

    // One primary suggestion, alternatives on demand (owner feedback
    // 2026-07-26: a booked-verifikat match next to history cards read as
    // noise). The ladder already ranks — the first suggestion IS the
    // answer; the rest collapse behind "Visa fler förslag".
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);
    useEffect(() => { setShowAllSuggestions(false); }, [line.id]);
    const visibleSuggestions = showAllSuggestions ? suggestions : suggestions.slice(0, 1);
    const hiddenCount = suggestions.length - visibleSuggestions.length;

    // Facts demoted to one muted meta line (owner feedback 2026-07-26:
    // the answer, not a form, leads the pane). Date + description live in
    // the header; the rest reads as small print under it.
    const metaParts = [
        line.reference ? `Referens ${line.reference}` : null,
        line.account_number
            ? `Konto ${line.account_number}${bankLeg ? ` → bokförs på ${bankLeg.account_number}` : ''}`
            : null,
        line.balance != null && line.balance !== '' ? `Saldo ${formatCurrency(parseFloat(line.balance))}` : null,
    ].filter(Boolean);

    return (
        // Sticky viewport-capped inspector beside the list; the
        // .bank-inspector class slides it in from the right on mount.
        <div
            className="bank-inspector"
            style={{
                position: 'sticky',
                top: 16,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 110px)',
                overflow: 'hidden',
            }}
        >
            {/* Fixed header zone: the transaction itself. */}
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                <div className="d-flex align-items-start justify-content-between" style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: isInflow ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(amount)}
                        </span>
                        <StatusBadge line={line} />
                    </div>
                    <Button variant="link" size="sm" onClick={onClose} style={{ color: '#6b7280', padding: 4 }} aria-label="Stäng">
                        <XIcon size={18} />
                    </Button>
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#374151' }}>
                    {formatDate(line.transaction_date)} · {line.description || '(ingen beskrivning)'}
                </div>
                {metaParts.length > 0 && (
                    <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                        {metaParts.join(' · ')}
                    </div>
                )}
                {/* The contextual assistant entry point. Deliberately the SMALLEST coherent one:
                    a link that hands this transaction to the existing assistant page, rather than
                    a second chat surface inside the inspector. The descriptor it carries is built
                    from `line.id` — the selection state this page already owns — never from the
                    text above it, which is bank-authored and attacker-controlled. */}
                {onAskAssistant && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem', marginTop: 8, textDecoration: 'none' }}
                        onClick={() => onAskAssistant(line)}
                    >
                        <Sparkles size={13} className="me-1" />
                        Fråga assistenten om den här händelsen
                    </Button>
                )}
            </div>

            {/* Scrolling body zone: the verdict + actions. The key retriggers
                the entrance animation on every row swap / data arrival, so
                content changes read as motion, not a flash. */}
            <div
                key={`${line.id}:${suggestionsLoading ? 'loading' : 'ready'}`}
                className="bank-pane-in"
                style={{ padding: '18px 20px 20px', overflowY: 'auto' }}
            >

                {/* ── Att granska: the verdict leads — no jargon header ──── */}
                {tier === TIER_REVIEW && (
                    <>
                        {suggestionsLoading && (
                            <div style={{ padding: '12px 0', color: '#6b7280', fontSize: '0.875rem' }}>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Söker efter matchningar…
                            </div>
                        )}
                        {!suggestionsLoading && suggestionsError && (
                            <Alert variant="warning" className="py-2" style={{ fontSize: '0.8125rem' }}>
                                <AlertTriangle size={14} className="me-2" />
                                {suggestionsError}
                            </Alert>
                        )}
                        {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && (
                            <p style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                {rejectedCount > 0
                                    ? 'Du har avvisat förslagen för den här raden.'
                                    : 'Inga matchningsförslag hittades. Skapa ett verifikat manuellt nedan.'}
                            </p>
                        )}
                        {!suggestionsLoading && rejectedCount > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    style={{ fontSize: '0.8125rem' }}
                                    onClick={onRestoreRejections}
                                    disabled={actionBusy}
                                >
                                    <Undo2 size={13} className="me-1" />
                                    Visa avvisade förslag igen
                                </Button>
                            </div>
                        )}
                        {/* ONE primary suggestion (the ladder's top pick);
                            alternatives fold behind "Visa fler förslag".
                            When a booked verifikat already matches this line,
                            history alternatives are context only — booking
                            again duplicates (walkthrough finding 2026-07-20). */}
                        {!suggestionsLoading && visibleSuggestions.map((suggestion, index) => {
                            const key = `${suggestion.matchStrategy || suggestion.strategy || 's'}-${suggestion.candidateId || index}`;
                            if ((suggestion.matchStrategy || suggestion.strategy) === 'history') {
                                return (
                                    <HistorySuggestionCard
                                        key={key}
                                        suggestion={suggestion}
                                        bankLeg={bankLeg}
                                        busy={actionBusy}
                                        onPrefillBooking={onPrefillHistoryBooking}
                                        onOpenNeighbor={onOpenNeighbor}
                                        onReject={onRejectSuggestion}
                                        guidanceOnly={suggestions.some(
                                            (s) => (s.matchStrategy || s.strategy) === 'verification')}
                                    />
                                );
                            }
                            return (
                                <InlineSuggestionCard
                                    key={key}
                                    suggestion={suggestion}
                                    bankLeg={bankLeg}
                                    companyId={companyId}
                                    busy={actionBusy}
                                    onConfirm={onConfirmSuggestion}
                                    onBookTransfer={onBookTransfer}
                                    onReject={onRejectSuggestion}
                                    guidanceOnly={suggestions.some(
                                        (s) => (s.matchStrategy || s.strategy) === 'verification')}
                                />
                            );
                        })}
                        {!suggestionsLoading && (hiddenCount > 0 || showAllSuggestions) && suggestions.length > 1 && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted p-0 mb-2"
                                onClick={() => setShowAllSuggestions((v) => !v)}
                            >
                                {showAllSuggestions ? (
                                    <><ChevronUp size={14} className="me-1" />Dölj övriga förslag</>
                                ) : (
                                    <><ChevronDown size={14} className="me-1" />Visa {hiddenCount} {hiddenCount === 1 ? 'förslag till' : 'fler förslag'}</>
                                )}
                            </Button>
                        )}

                        {/* Global-priors evidence (§4.6b): cross-tenant advisory,
                            shown only when the own-history/rule ladder produced no
                            strong suggestion. Informational — never a confirm. */}
                        {!suggestionsLoading && !suggestionsError && prior
                            && !hasStrongSuggestion(suggestions) && (
                            <PriorsEvidenceLine prior={prior} />
                        )}

                        {/* AI-fallback (§4.1 L4): only offered when the ladder came
                            up empty or weak; hidden entirely when the endpoint does
                            not exist (404), disabled with a tooltip when the tenant
                            lacks AI credits (402/403). */}
                        {!suggestionsLoading && !suggestionsError && llm && llm.suggestion && (
                            <LlmSuggestionCard
                                suggestion={llm.suggestion}
                                busy={actionBusy}
                                onUse={onUseLlmSuggestion}
                            />
                        )}
                        {!suggestionsLoading && !suggestionsError && llm
                            && !llm.suggestion && !llm.unavailable
                            && !hasStrongSuggestion(suggestions) && (
                            <div className="mt-1 mb-1">
                                {llm.denied ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="llm-denied-tip">AI-krediter saknas för företaget.</Tooltip>}
                                    >
                                        <span className="d-inline-block">
                                            <Button size="sm" variant="outline-secondary" disabled style={{ pointerEvents: 'none' }}>
                                                <Sparkles size={14} className="me-1" />
                                                Fråga AI
                                            </Button>
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    <Button size="sm" variant="outline-primary" disabled={llm.loading || actionBusy} onClick={onAskAi}>
                                        {llm.loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-1" />
                                                Frågar AI…
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles size={14} className="me-1" />
                                                Fråga AI
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Quiet footer zone: the always-available escape
                            hatches, visually separated from the verdict. */}
                        <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 20, paddingTop: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            <Button variant="link" size="sm" className="text-muted p-1" disabled={actionBusy} onClick={onCreateVerification}>
                                <FilePlus size={14} className="me-1" />
                                Skapa verifikat manuellt
                            </Button>
                            <Button variant="link" size="sm" className="text-muted p-1" disabled={actionBusy} onClick={onMarkReconciledExternal}>
                                <CheckCircle2 size={14} className="me-1" />
                                Markera som avstämd externt
                            </Button>
                        </div>
                    </>
                )}

                {/* ── Att bekräfta: verdict + one action ────────────────── */}
                {tier === TIER_MATCH && (
                    <>
                        {/* Same lead words as the "Redan bokförd?"-suggestion
                            cards in the pile — the only difference is
                            certainty: here the engine already made the link
                            (self-originated evidence), there it proposes one. */}
                        <Eyebrow color="#b45309">Redan bokförd</Eyebrow>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 10 }}>
                            {line.verification_id
                                ? 'Bokföringen finns redan — motorn har kopplat raden till verifikatet nedan. Bekräfta så är raden klar. Inget nytt bokförs.'
                                : 'Motorn har matchat raden — bekräfta så markeras den som avstämd.'}
                        </p>
                        {linkedVerification && (
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#374151' }}>
                                    <FileText size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                    <strong>{linkedVerification.series}{linkedVerification.number}</strong>
                                    <span style={{ color: '#9ca3af' }}>·</span>
                                    <span>{formatDate(linkedVerification.date)}</span>
                                    {linkedVerification.description && (
                                        <>
                                            <span style={{ color: '#9ca3af' }}>·</span>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {linkedVerification.description}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <KonteringSlip rows={(linkedVerification.transactions || []).map((t) => ({
                                    account_number: t.account_number,
                                    account_name: t.account_name,
                                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                                }))} />
                            </div>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                            <Button variant="primary" size="sm" disabled={actionBusy} onClick={onConfirmSingle}>
                                <CheckCircle size={14} className="me-1" />
                                Bekräfta
                            </Button>
                            <Button variant="link" size="sm" className="text-muted" disabled={actionBusy} onClick={onReset}>
                                {actionBusy ? <Spinner animation="border" size="sm" className="me-1" /> : <Undo2 size={14} className="me-1" />}
                                Ångra matchning
                            </Button>
                        </div>
                    </>
                )}

                {/* ── Klara: verdict + read-only actions ────────────────── */}
                {tier === TIER_DONE && (
                    <>
                        <Eyebrow color="#10b981">Klar</Eyebrow>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 10 }}>
                            {line.verification_id
                                ? 'Raden är bokförd och kopplad till ett verifikat.'
                                : 'Raden är avstämd — inget verifikat behövdes.'}
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                            {line.verification_id && (
                                <Button variant="outline-primary" size="sm" disabled={actionBusy} onClick={onShowVerification}>
                                    <FileText size={14} className="me-1" />
                                    Visa verifikat
                                </Button>
                            )}
                            {canReset && (
                                <Button variant="link" size="sm" className="text-muted" disabled={actionBusy} onClick={onReset}>
                                    {actionBusy ? <Spinner animation="border" size="sm" className="me-1" /> : <Undo2 size={14} className="me-1" />}
                                    Ångra
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// Initial state for the per-line AI-fallback (§4.1 L4).
const LLM_IDLE = { loading: false, suggestion: null, denied: false, unavailable: false };

export default function BankhandelserPage() {
    const { companyId } = useParams();
    const routerHistory = useHistory();
    const { features } = useFeatures();
    // AI progressive enhancement (B9): the page itself is gated on
    // banking_integration only; ai_reconciliation lights up the AI-heavy
    // affordances and the Automatisering settings link. Byråöversikt is
    // hidden from the UI (route remains behind byra_tier — plan §4.7).
    const hasAiReconciliation = Boolean(features?.ai_reconciliation);
    // PSD2 feed (bank_feed): when the tenant has a live bank connection the
    // header shows sync freshness/consent health instead of pretending CSV
    // import is the only way in. Connection MANAGEMENT lives in Settings →
    // Bankanslutningar; this is status-only.
    const hasBankFeed = Boolean(features?.bank_feed);
    // The contextual assistant entry point rides the same entitlement as the assistant page it
    // navigates to — offering a link to a route the tenant cannot open would be a dead end.
    const hasAiAssistant = Boolean(features?.ai_assistant);
    const [feedConnections, setFeedConnections] = useState([]);

    useEffect(() => {
        if (!hasBankFeed || !companyId) return;
        let cancelled = false;
        import('../../../services/BankConnectionService').then(({ fetchConnections }) =>
            fetchConnections(companyId)
                .then((conns) => {
                    if (!cancelled) setFeedConnections(conns);
                })
                .catch(() => {})
        );
        return () => {
            cancelled = true;
        };
    }, [hasBankFeed, companyId]);

    const [statements, setStatements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Filter/search/sort in the URL. FILTER_ATTENTION is the default, so the
    // clean /bankstatements URL still opens on "what needs me" without
    // carrying ?filter=attention around.
    const {
        search: searchText,
        setSearch: setSearchText,
        debouncedSearch,
        filter: activeFilter,
        setFilter: setActiveFilter,
        sort: sortKey,
        setSort: setSortKey,
    } = useListControls({ defaultFilter: FILTER_ATTENTION, defaultSort: 'date_desc' });
    const [selectedId, setSelectedId] = useState(null);
    const [rejectedKeys, setRejectedKeys] = useState(() => new Set());
    const [rejectDialog, setRejectDialog] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    // Server-side needs-attention counts (B5) — the summary strip.
    const [attentionCounts, setAttentionCounts] = useState(null);

    // One silent engine run per mount when the list contains rows the
    // engine never saw (pre-B3 imports, changed rules) — replaces the old
    // "Kör matchning" button as a user concept.
    const autoRunRef = useRef(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [showCreateVerificationModal, setShowCreateVerificationModal] = useState(false);
    const [verificationPrefillRows, setVerificationPrefillRows] = useState(null);

    // Batch actions (owner iterations 2026-07-26/27): minimum interaction,
    // nothing hidden. 'Koppla alla' lives under the Att koppla pill (the set
    // IS the filtered list); previous-years dismissal is a quiet one-liner
    // with undo in the toast.
    const [linkAllBusy, setLinkAllBusy] = useState(false);
    // Per-row batch feedback (owner 2026-07-27): the row being processed
    // shows the same dim+spinner as row-loading; finished rows fade out.
    const [linkProgress, setLinkProgress] = useState(null); // { done, total }
    const [processingId, setProcessingId] = useState(null);
    const [leavingIds, setLeavingIds] = useState(() => new Set());
    const [dismissOldBusy, setDismissOldBusy] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);


    // bank account number → mapped BAS account (bank_account_mappings).
    // Drives the account-context line, the detail meta, and — critically —
    // the payment leg of every booking started from this page (owner
    // feedback 2026-07-26: the mapped konto must be used; the tenant's
    // primary_bank chart role is the only fallback — never a hardcoded
    // number, and an unresolved role is an actionable state).
    const [accountMappings, setAccountMappings] = useState({});
    const [roleLegs, setRoleLegs] = useState(null);
    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        resolveRoleAccounts(companyId, ['primary_bank', 'tax_account']).then((legs) => {
            if (active) setRoleLegs(legs);
        });
        return () => { active = false; };
    }, [companyId]);
    const roleBankLeg = roleLegs?.primary_bank
        ? { account_number: roleLegs.primary_bank.accountNumber, account_name: roleLegs.primary_bank.accountName }
        : null;
    const roleTaxLeg = roleLegs?.tax_account
        ? { account_number: roleLegs.tax_account.accountNumber, account_name: roleLegs.tax_account.accountName }
        : null;
    const bankLegFor = useCallback(
        (bankAccountNumber) => accountMappings[bankAccountNumber] || null,
        [accountMappings],
    );

    // Inline suggestions for the selected Att granska line (§4.1 surface).
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionGroupId, setSuggestionGroupId] = useState(null);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsError, setSuggestionsError] = useState(null);
    const suggestionFetchToken = useRef(0);

    // AI-fallback state for the selected line. `unavailable` (404) hides
    // the button for the session — the endpoint is being rolled out in
    // parallel and may simply not exist yet.
    const [llmState, setLlmState] = useState(LLM_IDLE);
    const llmUnavailableRef = useRef(false);

    // Community-prior evidence for the selected line (§4.6b) — set from
    // the unified-suggestions response (B4), no separate fetch.
    const [prior, setPrior] = useState(null);
    // Inline verifikat for engine-linked to-confirm rows (owner 2026-07-27:
    // the pane must SHOW the matched verifikat, not offer a click).
    const [linkedVerification, setLinkedVerification] = useState(null);
    // Rejection-suppression escape hatch: >0 means förslag are hidden by
    // earlier Avvisa-clicks and can be brought back.
    const [rejectedCount, setRejectedCount] = useState(0);

    // "Visa verifikat" — full verification row for VerificationDetailsModal.
    const [showVerificationDetails, setShowVerificationDetails] = useState(false);
    const [verificationRow, setVerificationRow] = useState(null);

    // Server-reported total backlog size, when the backend sends one —
    // used only for the "Visar X av Y" notice (defensive: 0/absent means
    // we simply don't show it).
    const [totalCount, setTotalCount] = useState(0);
    const [totalIsServer, setTotalIsServer] = useState(false);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const data = await listBankStatements(companyId, { limit: QUEUE_FETCH_LIMIT });
            const fetched = data?.statements || [];
            setStatements(fetched);
            const { total, isServerTotal } = extractListTotal(data, fetched.length);
            setTotalCount(total);
            setTotalIsServer(isServerTotal);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte hämta banktransaktioner');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
        // Strip + banner data ride along with every list refresh; failures
        // are silent — a missing banner must never block the queue.
        getAttentionCounts(companyId).then(setAttentionCounts);
        getBankAccountMappings(companyId).then(setAccountMappings);
    }, [companyId]);

    useEffect(() => {
        setIsLoading(true);
        load();
    }, [load]);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        load();
    }, [load]);

    // "Ladda fler" (the Alla ledger view): append the next server page.
    const [loadingMore, setLoadingMore] = useState(false);
    const handleLoadMore = useCallback(async () => {
        setLoadingMore(true);
        try {
            const data = await listBankStatements(companyId, {
                limit: QUEUE_FETCH_LIMIT,
                offset: statements.length,
            });
            const fetched = data?.statements || [];
            setStatements((prev) => {
                const seen = new Set(prev.map((l) => l.id));
                return [...prev, ...fetched.filter((l) => !seen.has(l.id))];
            });
        } catch (err) {
            toast.error('Kunde inte ladda fler transaktioner');
        } finally {
            setLoadingMore(false);
        }
    }, [companyId, statements.length]);

    // Silent engine run when rows exist the engine never snapshotted
    // (suggestion_snapshot null on an unmatched row = never computed).
    const [autoMatchBusyBg, setAutoMatchBusyBg] = useState(false);
    useEffect(() => {
        if (autoRunRef.current || isLoading) return;
        const unseen = statements.some(
            (l) => l.reconciliation_status === 'unmatched'
                && (l.suggestion_snapshot === null || l.suggestion_snapshot === undefined),
        );
        if (!unseen) return;
        autoRunRef.current = true;
        setAutoMatchBusyBg(true);
        runAutoMatchAll(companyId)
            .then((data) => {
                const s = data?.result?.summary;
                if (s && (s.autoMatched > 0 || s.suggested > 0)) {
                    toast.success(`${s.autoMatched} transaktioner matchades automatiskt, ${s.suggested} fick förslag.`);
                }
                load();
            })
            .catch(() => {})
            .finally(() => setAutoMatchBusyBg(false));
    }, [statements, isLoading, companyId, load]);

    const distinctAccounts = useMemo(
        () => [...new Set(statements.map((l) => l.account_number).filter(Boolean))],
        [statements],
    );

    // Current saldo per account = balance on the NEWEST row that carries
    // one (owner 2026-07-27: the page never showed the saldo). Computed
    // over the full loaded set — the date filter must not change a
    // point-in-time fact.
    const accountBalances = useMemo(() => {
        const latest = {};
        for (const line of statements) {
            if (!line.account_number || line.balance == null || line.balance === '') continue;
            const prev = latest[line.account_number];
            const d = String(line.transaction_date || '');
            if (!prev || d > prev.date) {
                latest[line.account_number] = { date: d, balance: parseFloat(line.balance) };
            }
        }
        return latest;
    }, [statements]);

    // Per-account filter (multi-bank tenants): clicking an account chip
    // scopes the page to that account; klick again = all accounts.
    const [activeAccount, setActiveAccount] = useState(null);

    // Date-range noise filter (owner 2026-07-27): scope EVERYTHING on the
    // page — pills, counts, batch actions — to the chosen window.
    const [dateRange, setDateRange] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const dateBounds = useMemo(() => {
        const iso = (d) => d.toISOString().slice(0, 10);
        const today = new Date();
        switch (dateRange) {
            case 'm1': { const d = new Date(today); d.setMonth(d.getMonth() - 1); return { from: iso(d), to: null }; }
            case 'm3': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: null }; }
            case 'year': return { from: `${today.getFullYear()}-01-01`, to: null };
            case 'custom': return { from: customFrom || null, to: customTo || null };
            default: return { from: null, to: null };
        }
    }, [dateRange, customFrom, customTo]);
    const dateFilteredStatements = useMemo(() => {
        return statements.filter((l) => {
            if (activeAccount && l.account_number !== activeAccount) return false;
            if (!dateBounds.from && !dateBounds.to) return true;
            const d = String(l.transaction_date || '').slice(0, 10);
            if (dateBounds.from && d < dateBounds.from) return false;
            if (dateBounds.to && d > dateBounds.to) return false;
            return true;
        });
    }, [statements, dateBounds, activeAccount]);

    const filterGroups = useMemo(() => {
        const groups = {
            [FILTER_ATTENTION]: [],
            [FILTER_MATCHED]: [],
            [FILTER_ALL]: dateFilteredStatements,
            [FILTER_DONE]: [],
        };
        for (const line of dateFilteredStatements) {
            const state = deriveDisplayState(line);
            // DISJOINT piles (owner 2026-07-27: subset pills read as
            // duplicates): Att koppla = the engine found an existing
            // booking (mechanical work); Att hantera = needs judgment;
            // Klara = done. Every row lives in exactly ONE work pile.
            if (isBatchConfirmable(line) || isLinkSafe(line)) {
                groups[FILTER_MATCHED].push(line);
            } else if (ATTENTION_STATES.includes(state)) {
                groups[FILTER_ATTENTION].push(line);
            } else {
                groups[FILTER_DONE].push(line);
            }
        }
        return groups;
    }, [dateFilteredStatements]);

    // Previous-years rows still open, EXCLUDING those with a found booking
    // (those belong under Att koppla → Koppla alla). Users who never imported
    // old SIE files know these are booked elsewhere — give them the exit.
    const priorYearCutoff = `${new Date().getFullYear()}-01-01`;
    const oldOpenLines = useMemo(
        () => (filterGroups[FILTER_ATTENTION] || []).filter((l) =>
            String(l.transaction_date || '') < priorYearCutoff
            && !isBatchConfirmable(l) && !isLinkSafe(l)),
        [filterGroups, priorYearCutoff],
    );



    const visibleLines = useMemo(() => {
        const lines = filterGroups[activeFilter] || [];
        const needle = debouncedSearch.toLowerCase();
        const filtered = !needle ? lines : lines.filter((l) =>
            (l.description || '').toLowerCase().includes(needle)
            || (l.reference || '').toLowerCase().includes(needle)
            || String(l.amount || '').includes(needle));

        const sorted = [...filtered];
        const dateOf = (l) => String(l.transaction_date || '');
        switch (sortKey) {
            case 'date_asc': sorted.sort((a, b) => dateOf(a).localeCompare(dateOf(b))); break;
            case 'amount_desc': sorted.sort((a, b) => Math.abs(parseFloat(b.amount)) - Math.abs(parseFloat(a.amount))); break;
            case 'amount_asc': sorted.sort((a, b) => Math.abs(parseFloat(a.amount)) - Math.abs(parseFloat(b.amount))); break;
            case 'state': sorted.sort((a, b) =>
                (STATE_ORDER[deriveDisplayState(a)] ?? 9) - (STATE_ORDER[deriveDisplayState(b)] ?? 9)
                || dateOf(b).localeCompare(dateOf(a))); break;
            default: sorted.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
        }
        return sorted;
    }, [filterGroups, activeFilter, debouncedSearch, sortKey]);

    // Windowed rendering (owner feedback 2026-07-26 — the list was a wall):
    // 50 rows at a time, "Visa fler" extends. Resets whenever the view
    // changes so a filter switch always starts at the top slice.
    const PAGE_SIZE = 50;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeFilter, debouncedSearch, sortKey, dateRange, customFrom, customTo, activeAccount]);
    const windowedLines = useMemo(
        () => visibleLines.slice(0, visibleCount),
        [visibleLines, visibleCount],
    );

    const selectedLine = useMemo(
        () => visibleLines.find((l) => l.id === selectedId) || null,
        [visibleLines, selectedId],
    );
    const selectedPaneMode = selectedLine ? paneModeFor(deriveDisplayState(selectedLine)) : null;

    // TASK CONTEXT (docs/TASK_CONTEXT_REGISTRY.md §5). Attention is the reconciliation queue (S3);
    // the other filters retain the ordinary bank-transaction detail surface (S1). Derived from
    // `selectedLine` — the selection state this page already owns — so it REPLACES itself when the
    // user picks another row and CLEARS when the page unmounts. It is a hint about what the user
    // is looking at, never a capability: the gateway validates it against the closed registry and
    // the backend authorizes the read independently of it.
    const taskDescriptor = useMemo(
        () => selectedLine
            ? (activeFilter === FILTER_ATTENTION
                ? reconciliationQueueItemDescriptor(selectedLine)
                : bankTransactionDescriptor(selectedLine))
            : null,
        [selectedLine, activeFilter],
    );
    useSurfaceTaskDescriptor(taskDescriptor);

    /**
     * Hand this transaction to the assistant.
     *
     * Through ROUTER STATE rather than the live store, because navigating away unmounts this page
     * and clears the store before the assistant route mounts — the clear is the point, and the
     * hand-off has to survive it. The assistant re-validates what it receives.
     */
    const handleAskAssistant = useCallback((line) => {
        const descriptor = activeFilter === FILTER_ATTENTION
            ? reconciliationQueueItemDescriptor(line)
            : bankTransactionDescriptor(line);
        if (!descriptor) return;
        routerHistory.push(`/${companyId}/ai-assistant`, handOffState(descriptor));
    }, [activeFilter, companyId, routerHistory]);

    // Deferred open (owner feedback 2026-07-26): the panel waits for its
    // data. Clicking a review-mode row sets pendingId — the ROW shows a
    // spinner while unified-suggestions loads, and the panel only opens
    // (setSelectedId) once the answer is in (or errored, so the pane can
    // say so). Non-review rows have nothing to fetch and open instantly.
    // Stale-response guard (audit A4): the token invalidates in-flight
    // responses the moment another row is clicked.
    const [pendingId, setPendingId] = useState(null);

    const handleSelectRow = useCallback((line) => {
        if (line.id === selectedId || line.id === pendingId) return;
        const mode = paneModeFor(deriveDisplayState(line));
        if (mode !== TIER_REVIEW) {
            suggestionFetchToken.current += 1; // cancel any pending fetch
            setPendingId(null);
            setSelectedId(line.id);
            return;
        }
        setPendingId(line.id);
        // Panel already open → swap it to the new row NOW (header renders
        // from list data, body shows loading) instead of lingering on the
        // old row's answer while the new one fetches (owner feedback
        // 2026-07-26). Panel closed → stays closed until data is ready.
        if (selectedId) setSelectedId(line.id);
    }, [selectedId, pendingId]);

    useEffect(() => {
        if (!pendingId) return;
        const token = ++suggestionFetchToken.current;
        // Fresh pane state for the incoming row. A 404 on the AI endpoint
        // sticks for the whole session via llmUnavailableRef.
        setSuggestions([]);
        setSuggestionGroupId(null);
        setSuggestionsError(null);
        setSuggestionsLoading(false);
        setPrior(null);
        setRejectedCount(0);
        setLinkedVerification(null);
        setLlmState({ ...LLM_IDLE, unavailable: llmUnavailableRef.current });
        setSuggestionsLoading(true);

        // Engine-linked rows carry verification_id — fetch the verifikat so
        // the pane can show it inline (rows + konton) before Bekräfta.
        const pendingLine = statements.find((l) => l.id === pendingId);
        if (pendingLine?.verification_id) {
            getStatementVerification(companyId, pendingId)
                .then((data) => {
                    if (suggestionFetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || null);
                })
                .catch(() => {});
        }

        getUnifiedSuggestions(companyId, pendingId)
            .then((data) => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestions(data?.suggestions || []);
                setSuggestionGroupId(data?.suggestion_group_id || null);
                setPrior(data?.prior || null);
                setRejectedCount(data?.rejected_count || 0);
            })
            .catch(() => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestionsError('Kunde inte hämta matchningsförslag');
            })
            .finally(() => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestionsLoading(false);
                setPendingId(null);
                setSelectedId(pendingId);
            });
    }, [companyId, pendingId, statements]);

    // After any mutation (confirm / verifikat / reset / batch) — refetch so
    // tiers reshuffle, and drop the selection since the line likely moved.
    const handleMutated = useCallback(() => {
        setSelectedId(null);
        setShowCreateVerificationModal(false);
        setVerificationPrefillRows(null);
        setIsRefreshing(true);
        load();
    }, [load]);

    // "Skapa regel?"-CTA (§4.5 rule synthesis): after a confirm/booking
    // succeeds, fire-and-forget the proposals endpoint; when one matches
    // the just-processed line's counterparty, offer it as a toast with
    // [Skapa regel] (→ Regler prefilled via router state) and [Avvisa]
    // (persisted dismissal; a 503 means NOT dismissed — surfaced).
    const offerRuleProposal = useCallback((line) => {
        if (!line) return;
        getRuleProposals(companyId)
            .then((data) => {
                const proposal = findMatchingProposal(data?.proposals, line);
                if (!proposal) return;
                toast(
                    `Du har bokfört ${proposal.counterparty} likadant ${proposal.occurrences} gånger — skapa en regel?`,
                    {
                        duration: 15000,
                        action: {
                            label: 'Skapa regel',
                            onClick: () => routerHistory.push({
                                pathname: `/${companyId}/settings/automation`,
                                state: { prefillRule: proposalToFormRule(proposal) },
                            }),
                        },
                        cancel: {
                            label: 'Avvisa',
                            onClick: () => {
                                dismissRuleProposal(companyId, proposal.counterparty)
                                    .then(() => toast.success('Regelförslaget avvisades.'))
                                    .catch((err) => toast.error(
                                        err?.response?.status === 503
                                            ? 'Avvisandet kunde inte sparas — förslaget kan dyka upp igen.'
                                            : (err?.response?.data?.message || 'Kunde inte avvisa regelförslaget'),
                                    ));
                            },
                        },
                    },
                );
            })
            .catch(() => {}); // best-effort — the CTA must never block the flow
    }, [companyId, routerHistory]);

    // Group-resolve offer: after ONE booking, offer to book the row's
    // unmatched same-counterparty siblings likadant (server scales the
    // kontering to each row's amount). Best-effort — never blocks.
    const offerBookSimilar = useCallback((line) => {
        if (!line) return;
        getSimilarCount(companyId, line.id).then((count) => {
            if (!count) return;
            toast(
                `${count} liknande ${count === 1 ? 'rad väntar' : 'rader väntar'} — bokför likadant?`,
                {
                    duration: 15000,
                    action: {
                        label: `Bokför ${count} liknande`,
                        onClick: () => {
                            bookSimilar(companyId, line.id)
                                .then((result) => {
                                    const booked = result?.booked || 0;
                                    if (booked > 0) toast.success(`${booked} liknande ${booked === 1 ? 'rad' : 'rader'} bokfördes.`);
                                    if (result?.skipped?.length > 0) {
                                        toast.info(`${result.skipped.length} kunde inte bokföras och ligger kvar i Att hantera.`);
                                    }
                                    handleMutated();
                                })
                                .catch((err) => toast.error(
                                    err?.response?.data?.message || 'Kunde inte bokföra de liknande raderna',
                                ));
                        },
                    },
                },
            );
        });
    }, [companyId, handleMutated]);

    const handleVerificationSuccess = useCallback(() => {
        toast.success('Verifikatet skapades.');
        const line = selectedLine;
        handleMutated();
        offerBookSimilar(line);
        offerRuleProposal(line);
    }, [handleMutated, selectedLine, offerBookSimilar, offerRuleProposal]);

    // Import "Klar" — the import response carries the auto-matching outcome
    // (B3): tell the user what the engine already did for them.
    const handleImportSuccess = useCallback((result) => {
        setShowImportModal(false);
        const s = result?.matching?.summary;
        if (s && (s.autoMatched > 0 || s.suggested > 0)) {
            toast.success(
                `${s.autoMatched} transaktioner matchades automatiskt, ${s.suggested} fick förslag — se Att hantera.`,
                { duration: 8000 },
            );
        } else if (result?.matching?.mode === 'enqueued') {
            toast.info('Transaktionerna matchas i bakgrunden — uppdatera listan om en stund.');
        }
        handleMutated();
    }, [handleMutated]);

    // Confirm one inline suggestion — the single confirm path for ALL
    // strategy types (POST .../confirm-suggestion).
    const handleConfirmSuggestion = useCallback(async (suggestion, { registerPayment = false } = {}) => {
        if (!selectedLine) return;
        const targets = buildConfirmTargets(suggestion);
        if (targets.length === 0) return;
        setActionBusy(true);
        try {
            const result = await confirmSuggestion(companyId, selectedLine.id, {
                suggestionGroupId,
                strategy: suggestion.matchStrategy || suggestion.strategy,
                targets,
                registerPayment,
            });
            if (registerPayment) {
                if (result?.payment?.success) {
                    toast.success('Fakturan markerades som betald och betalningen bokfördes.');
                } else {
                    toast.success('Matchningen bekräftades.');
                    toast.error(`Betalningen kunde inte registreras: ${result?.payment?.message || 'okänt fel'} — registrera den från fakturan.`, { duration: 10000 });
                }
            } else {
                toast.success('Matchningen bekräftades.');
            }
            const line = selectedLine;
            handleMutated();
            offerRuleProposal(line);
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error('Redan matchad — uppdatera listan.');
                handleMutated();
            } else {
                toast.error(err?.response?.data?.message || err.message || 'Kunde inte bekräfta matchningen');
            }
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, suggestionGroupId, handleMutated, offerRuleProposal]);

    // Transfer suggestions have no linkable target — "confirm" is booking
    // the transfer verifikat (skattekonto ↔ the line's MAPPED bank konto).
    const handleBookTransfer = useCallback((suggestion) => {
        if (!selectedLine) return;
        const amount = round2(Math.abs(parseFloat(selectedLine.amount)));
        const incoming = parseFloat(selectedLine.amount) > 0;
        const leg = bankLegFor(selectedLine.account_number) || roleBankLeg;
        // Both legs come from the tenant's own chart (mapping or role) —
        // an unresolved role is surfaced, never replaced by a pinned number.
        if (!leg || !roleTaxLeg) {
            toast.error(
                !leg
                    ? 'Rollen Företagskonto är inte mappad — mappa den under Inställningar → Bokföring → Kontoplansroller.'
                    : 'Rollen Skattekonto är inte mappad — mappa den under Inställningar → Bokföring → Kontoplansroller.'
            );
            return;
        }
        const bankRow = {
            account_number: leg.account_number,
            account_name: leg.account_name,
            debit: incoming ? amount : '',
            credit: incoming ? '' : amount,
        };
        const counterRow = {
            account_number: roleTaxLeg.account_number,
            account_name: roleTaxLeg.account_name,
            debit: incoming ? '' : amount,
            credit: incoming ? amount : '',
        };
        setVerificationPrefillRows([bankRow, counterRow]);
        setShowCreateVerificationModal(true);
    }, [selectedLine, bankLegFor, roleBankLeg, roleTaxLeg]);

    // History suggestion "Bokför så här" — an L3 candidate proposes a
    // BOOKING (scaled counter rows + our bank leg from the account
    // mapping), so it opens the wizard prefilled instead of hitting the
    // confirm-suggestion endpoint.
    const handlePrefillHistoryBooking = useCallback((suggestion) => {
        setVerificationPrefillRows(buildHistoryPrefillRows(
            suggestion,
            (selectedLine ? bankLegFor(selectedLine.account_number) : null) || roleBankLeg,
        ));
        setShowCreateVerificationModal(true);
    }, [selectedLine, bankLegFor, roleBankLeg]);

    // Neighbor chip ("V12 · 2026-03-04") — fetch the precedent verifikat
    // by id (GET /:companyId/verification/:id — the shared accounting
    // endpoint; neighbors are not statement-scoped) and open the modal.
    const handleOpenNeighbor = useCallback(async (neighbor) => {
        if (!neighbor?.id) return;
        setActionBusy(true);
        try {
            const verification = await getVerificationById(companyId, neighbor.id);
            // The endpoint returns the verification directly keyed on
            // verification_id (not id) — accept either.
            const vid = verification && (verification.verification_id || verification.id);
            if (!vid) {
                toast.error('Verifikatet kunde inte hämtas.');
                return;
            }
            let debitSum = 0;
            let creditSum = 0;
            (verification.transactions || []).forEach((t) => {
                const rowAmount = parseFloat(t.amount) || 0;
                if (t.transaction_type === 'debit') debitSum += rowAmount;
                else creditSum += rowAmount;
            });
            setVerificationRow({ ...verification, id: vid, debitSum: round2(debitSum), creditSum: round2(creditSum) });
            setShowVerificationDetails(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte hämta verifikatet');
        } finally {
            setActionBusy(false);
        }
    }, [companyId]);

    // "Fråga AI" — the entitlement-gated LLM fallback. 402/403 = no AI
    // credits (toast + disabled button w/ tooltip); 404 = endpoint absent
    // (hide for the session); anything else is a real error.
    const handleAskAi = useCallback(async () => {
        if (!selectedLine) return;
        setLlmState((prev) => ({ ...prev, loading: true }));
        try {
            const data = await getLlmSuggestion(companyId, selectedLine.id);
            const suggestion = data?.suggestion || data;
            if (!suggestion || (!Array.isArray(suggestion.rows) && !suggestion.rationale)) {
                setLlmState((prev) => ({ ...prev, loading: false }));
                toast.info('AI:n hittade inget förslag för den här transaktionen.');
                return;
            }
            setLlmState({ loading: false, suggestion, denied: false, unavailable: false });
        } catch (err) {
            const kind = llmDenialKind(err);
            if (kind === 'absent') {
                llmUnavailableRef.current = true;
                setLlmState({ ...LLM_IDLE, unavailable: true });
            } else if (kind === 'no_credits') {
                toast.error('AI-krediter saknas');
                setLlmState({ ...LLM_IDLE, denied: true });
            } else {
                setLlmState((prev) => ({ ...prev, loading: false }));
                toast.error(err?.response?.data?.message || err.message || 'AI-förslaget kunde inte hämtas');
            }
        }
    }, [companyId, selectedLine]);

    // AI-card confirm = prefill the booking wizard (the LLM proposes a
    // booking, not a match) — never the confirm-suggestion endpoint.
    const handleUseLlmSuggestion = useCallback((suggestion) => {
        const rows = normalizeSuggestionRows(suggestion?.rows);
        setVerificationPrefillRows(rows.length >= 2 ? rows : null);
        setShowCreateVerificationModal(true);
    }, []);

    // "Avvisa" — the learning loop's reject event (audit D6). Opens the
    // reason dialog; the actual submit lives in submitReject below.
    const handleRejectSuggestion = useCallback((suggestion) => {
        setRejectReason('');
        setRejectDialog(suggestion);
    }, []);

    const submitReject = useCallback(async () => {
        const suggestion = rejectDialog;
        if (!suggestion || !selectedLine) return;
        setActionBusy(true);
        try {
            await submitMatchingFeedback(companyId, {
                statementId: selectedLine.id,
                suggestionGroupId,
                event: 'rejected',
                reason: rejectReason.trim() || undefined,
                // Server-side suppression (Phase B): name the rejected
                // suggestion so it stays suppressed across sessions — the
                // rejectedKeys set below is now just an optimistic overlay.
                suggestion: {
                    strategy: suggestion.matchStrategy || suggestion.strategy || null,
                    candidate_id: suggestion.candidateId || null,
                    retrieval_source: suggestion.retrievalSource || null,
                    // Kontering signature (2026-07-26): rejecting THIS
                    // proposal must not hide a different one on the line.
                    rows: (suggestion.rows || suggestion.templateRows || []).map((r) => ({
                        account_number: r.account_number,
                        side: r.side || (parseFloat(r.debit) > 0 ? 'debit' : 'credit'),
                    })),
                },
            });
            setRejectedKeys((prev) => {
                const next = new Set(prev);
                next.add(rejectionKey(selectedLine.id, suggestion));
                return next;
            });
            setSuggestions((prev) => prev.filter((s) => s !== suggestion));
            setRejectedCount((c) => c + 1);
            setRejectDialog(null);
            toast.success('Förslaget avvisades.');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte avvisa förslaget');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, suggestionGroupId, rejectDialog, rejectReason]);

    // Review-tier escape hatch for lines reconciled outside LedgerLoop.
    // Undo rejection suppression ("Visa förslagen igen") — server deletes
    // the line's rejected-events + refreshes its snapshot; we refetch the
    // pane (setPendingId reruns the fetch) and the list badge in place
    // WITHOUT dropping the selection.
    const handleRestoreRejections = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            const result = await restoreRejectedSuggestions(companyId, selectedLine.id);
            const n = result?.restored || 0;
            toast.success(n > 0 ? 'Förslagen visas igen.' : 'Inga avvisade förslag att återställa.');
            setPendingId(selectedLine.id);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte återställa förslagen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, load]);

    const handleMarkReconciledExternal = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await bulkMarkReconciled(companyId, [selectedLine.id], {
                status: 'reconciled_external',
                notes: 'Markerad som avstämd externt via Matchning',
            });
            toast.success('Transaktionen markerades som avstämd externt.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte markera som avstämd');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // Matchade tier: confirm one line as reconciled (status → verified).
    const handleConfirmSingle = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await bulkMarkReconciled(companyId, [selectedLine.id], {
                status: 'verified',
                notes: 'Bekräftad via Matchning',
            });
            toast.success('Transaktionen bekräftades som avstämd.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte bekräfta transaktionen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // "Ångra" — reset a matched/skipped/externally-reconciled line back to
    // unmatched (the promised undo, audit D4). 409 = raced another change.
    const handleReset = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await resetStatement(companyId, selectedLine.id);
            toast.success('Raden återställdes till omatchad.');
            handleMutated();
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error('Kunde inte ångra — raden har ändrats. Uppdatera listan.');
                handleMutated();
            } else {
                toast.error(err?.response?.data?.message || err.message || 'Kunde inte ångra');
            }
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // "Visa verifikat" — fetch the statement's verification and open the
    // shared VerificationDetailsModal (read-only) with computed sums.
    const handleShowVerification = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            const data = await getStatementVerification(companyId, selectedLine.id);
            const verification = data?.verification;
            if (!verification) {
                toast.error('Inget verifikat hittades för raden.');
                return;
            }
            let debitSum = 0;
            let creditSum = 0;
            (verification.transactions || []).forEach((t) => {
                const rowAmount = parseFloat(t.amount) || 0;
                if (t.transaction_type === 'debit') debitSum += rowAmount;
                else creditSum += rowAmount;
            });
            setVerificationRow({ ...verification, debitSum: round2(debitSum), creditSum: round2(creditSum) });
            setShowVerificationDetails(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte hämta verifikatet');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine]);


    // "Koppla alla" (Att koppla pill): rows are processed ONE BY ONE so the
    // batch is visible work, not a spinner-and-pray — the active row dims
    // with a spinner, done rows fade out of Att koppla (their status flips
    // locally; a quiet reload at the end restores server truth). Scoped to
    // the rows currently in view (date window applies). Link-only and
    // per-row undoable, so no confirm dialog.
    const handleLinkAll = useCallback(async () => {
        const matchedLines = [...(filterGroups[FILTER_MATCHED] || [])];
        if (matchedLines.length === 0) return;
        setLinkAllBusy(true);
        setLinkProgress({ done: 0, total: matchedLines.length });
        let handled = 0;
        let alreadyDone = 0;
        let skipped = 0;
        try {
            for (const line of matchedLines) {
                setProcessingId(line.id);
                let ok = false;
                let already = false;
                try {
                    if (isBatchConfirmable(line)) {
                        await bulkMarkReconciled(companyId, [line.id], {
                            status: 'verified',
                            notes: 'Bekräftad via Koppla alla i Bankhändelser',
                        });
                        ok = true;
                    } else {
                        const result = await linkSafeMatches(companyId, [line.id]);
                        // A row someone else (another tab, an earlier run)
                        // already handled is DONE, not skipped — it fades
                        // out like a success (2026-07-27 incident).
                        already = (result?.already_handled || []).length > 0;
                        ok = (result?.linked || 0) > 0 || already;
                    }
                } catch (rowError) {
                    ok = false;
                }
                if (ok) {
                    if (already) alreadyDone += 1; else handled += 1;
                    // Fade the row out, then flip its local status so it
                    // leaves Matchade (server truth reloaded at the end).
                    setLeavingIds((prev) => new Set(prev).add(line.id));
                    setTimeout(() => {
                        setStatements((prev) => prev.map((l) =>
                            l.id === line.id ? { ...l, reconciliation_status: 'verified' } : l));
                        setLeavingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(line.id);
                            return next;
                        });
                    }, 300);
                } else {
                    skipped += 1;
                }
                setProcessingId(null);
                setLinkProgress((p) => ({ done: (p?.done || 0) + 1, total: p?.total || matchedLines.length }));
            }
            if (handled > 0) toast.success(`${handled} ${handled === 1 ? 'rad' : 'rader'} kopplade — inget nytt bokfördes.`);
            if (alreadyDone > 0) {
                toast.info(`${alreadyDone} ${alreadyDone === 1 ? 'rad var' : 'rader var'} redan klara sedan tidigare.`);
            }
            if (skipped > 0) {
                toast.info(
                    `${skipped} ${skipped === 1 ? 'rad hoppades' : 'rader hoppades'} över vid extrakontrollen och ligger kvar.`,
                    { duration: 8000 },
                );
            }
            load();
        } finally {
            setLinkAllBusy(false);
            setLinkProgress(null);
            setProcessingId(null);
        }
    }, [companyId, filterGroups, load]);

    // Previous-years dismissal (owner reasoning 2026-07-27: old rows are
    // often already booked in a previous system whose SIE was never
    // imported — the matcher CANNOT find those). Marks them avstämda
    // (reconciled_external, nothing booked); undo for the whole batch
    // lives in the toast, per-row Ångra under Klara.
    const handleDismissOld = useCallback(async () => {
        const ids = oldOpenLines.map((l) => l.id);
        if (ids.length === 0) return;
        setDismissOldBusy(true);
        try {
            await bulkMarkReconciled(companyId, ids, {
                status: 'reconciled_external',
                notes: 'Markerad som bokförd i tidigare system (rader från tidigare år)',
            });
            toast.success(`${ids.length} rader från tidigare år markerades som redan bokförda.`, {
                duration: 12000,
                action: {
                    label: 'Ångra',
                    onClick: () => {
                        bulkUnmatchStatements(companyId, ids)
                            .then(() => { toast.success('Raderna återställdes.'); handleMutated(); })
                            .catch(() => toast.error('Kunde inte ångra allt — använd Ångra per rad under Klara.'));
                    },
                },
            });
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte markera raderna');
        } finally {
            setDismissOldBusy(false);
        }
    }, [companyId, oldOpenLines, handleMutated]);

    // Summary-strip numbers: server-side attention counts when available,
    // else derived from the loaded window.
    // With an active date window the strip must describe what's VISIBLE —
    // the server-side counts are always all-time, so derive locally then.
    const dateWindowActive = Boolean(dateBounds.from || dateBounds.to);
    const stripCounts = (!dateWindowActive && attentionCounts) || {
        to_confirm: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_TO_CONFIRM).length,
        has_suggestion: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_SUGGESTED).length,
        needs_you: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_NEEDS_YOU).length,
    };
    const bookedCount = filterGroups[FILTER_DONE].length;

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PageHeader
                    icon={Landmark}
                    title="Bankhändelser"
                    subtitle={
                        feedConnections.length > 0
                            ? 'Granska och bokför dina banktransaktioner — hämtas automatiskt från banken.'
                            : 'Importera, granska och bokför dina banktransaktioner.'
                    }
                >
                    {feedConnections.length > 0 && (() => {
                        const newestSync = feedConnections
                            .map((c) => c.last_sync_at)
                            .filter(Boolean)
                            .sort()
                            .pop();
                        const needsRenewal = feedConnections.some(
                            (c) => c.status === 'expiring' || c.status === 'expired'
                        );
                        return (
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: needsRenewal ? '#b45309' : '#9ca3af' }}>
                                {needsRenewal ? (
                                    <Link to={`/${companyId}/settings/bank-connections`} style={{ color: '#b45309', fontWeight: 600 }}>
                                        Bankmedgivande behöver förnyas med BankID →
                                    </Link>
                                ) : (
                                    <>
                                        Senast hämtad från banken:{' '}
                                        {newestSync ? moment(newestSync).format('YYYY-MM-DD HH:mm') : 'väntar på första hämtningen'}
                                    </>
                                )}
                            </p>
                        );
                    })()}
                </PageHeader>

                {/* Summary strip (§4.3): what the system did vs what needs you */}
                {!isLoading && statements.length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '12px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#10b981' }} />
                            <strong>{bookedCount}</strong> bokförda/klara
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#b45309' }} />
                            <strong>{stripCounts.to_confirm}</strong> att bekräfta
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Sparkles size={15} style={{ color: 'var(--primary)' }} />
                            <strong>{stripCounts.has_suggestion}</strong> med förslag
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <AlertCircle size={15} style={{ color: '#dc2626' }} />
                            <strong>{stripCounts.needs_you}</strong> behöver dig
                        </span>
                        {autoMatchBusyBg && (
                            <span style={{ fontSize: 12, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Spinner animation="border" size="sm" />
                                Matchar…
                            </span>
                        )}
                    </div>
                )}

                {/* Bank-account context (owner feedback 2026-07-26): which
                    bankkonto these rows belong to and WHICH kontoplan-konto
                    it books against. Unmapped accounts get the inline
                    "koppla" nudge — bookings fall back to the primary_bank role until then. */}
                {distinctAccounts.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {distinctAccounts.map((acct) => {
                            const leg = accountMappings[acct];
                            return leg ? (
                                <span
                                    key={acct}
                                    role={distinctAccounts.length > 1 ? 'button' : undefined}
                                    onClick={distinctAccounts.length > 1
                                        ? () => { setActiveAccount((a) => (a === acct ? null : acct)); setSelectedId(null); }
                                        : undefined}
                                    title={distinctAccounts.length > 1 ? 'Visa endast detta konto' : undefined}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        border: activeAccount === acct ? '1px solid var(--primary)' : '1px solid #e5e7eb',
                                        background: activeAccount === acct ? 'var(--rgba-primary-1)' : '#fff',
                                        borderRadius: 999, padding: '4px 12px',
                                        fontSize: 12, color: '#374151',
                                        fontVariantNumeric: 'tabular-nums',
                                        cursor: distinctAccounts.length > 1 ? 'pointer' : 'default',
                                    }}
                                >
                                    <Landmark size={12} style={{ color: '#6b7280' }} />
                                    {acct}
                                    <span style={{ color: '#9ca3af' }}>bokförs på</span>
                                    <strong>{leg.account_number}</strong>
                                    <span style={{ color: '#6b7280' }}>{leg.account_name}</span>
                                    {accountBalances[acct] && (
                                        <>
                                            <span style={{ color: '#9ca3af' }}>·</span>
                                            <span style={{ color: '#9ca3af' }}>Saldo</span>
                                            <strong>{formatCurrency(accountBalances[acct].balance)}</strong>
                                        </>
                                    )}
                                </span>
                            ) : (
                                <Link
                                    key={acct}
                                    to={`/${companyId}/bank-accounts`}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        border: '1px solid #fcd34d', background: '#fffbeb',
                                        borderRadius: 999, padding: '4px 12px',
                                        fontSize: 12, color: '#92400e', textDecoration: 'none',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                    title="Utan koppling bokförs raderna mot rollen Företagskonto — koppla kontot till din kontoplan"
                                >
                                    <AlertTriangle size={12} />
                                    {acct}
                                    <span>saknar konto i kontoplanen — koppla</span>
                                </Link>
                            );
                        })}
                    </div>
                )}

                <ListToolbar
                    search={{
                        value: searchText,
                        onChange: setSearchText,
                        placeholder: 'Sök beskrivning, referens, belopp…',
                        ariaLabel: 'Sök bland bankhändelser',
                    }}
                    sort={{
                        options: SORT_OPTIONS,
                        value: sortKey,
                        onChange: (v) => setSortKey(v || 'date_desc'),
                        ariaLabel: 'Sortera bankhändelser',
                    }}
                    actions={
                        <>
                            <Button variant="outline-secondary" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                                <RefreshCw size={14} className={isRefreshing ? 'me-1 spin' : 'me-1'} />
                                Uppdatera
                            </Button>
                            <Button variant="primary" size="sm" onClick={() => setShowImportModal(true)}>
                                <Upload size={14} className="me-1" />
                                Importera
                            </Button>
                            {/* Rules/auto-post/AI-historik config lives in
                                Inställningar → Automatisering (Phase D) */}
                            {hasAiReconciliation && (
                                <Button
                                    as={Link}
                                    to={`/${companyId}/settings/automation`}
                                    variant="outline-secondary"
                                    size="sm"
                                    title="Regler och automatisering"
                                >
                                    <SlidersHorizontal size={14} className="me-1" />
                                    Automatisering
                                </Button>
                            )}
                        </>
                    }
                >
                    <div style={{ minWidth: 170 }}>
                        <Select
                            options={DATE_RANGE_OPTIONS}
                            value={DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) || DATE_RANGE_OPTIONS[0]}
                            onChange={(opt) => setDateRange(opt?.value || 'all')}
                            isSearchable={false}
                            theme={brandSelectTheme}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            styles={{
                                control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
                                option: (base) => ({ ...base, fontSize: 13 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            aria-label="Filtrera på datum"
                        />
                    </div>
                    {dateRange === 'custom' && (
                        <>
                            <Form.Control
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                aria-label="Från datum"
                                style={{ width: 150 }}
                            />
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>–</span>
                            <Form.Control
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                                aria-label="Till datum"
                                style={{ width: 150 }}
                            />
                        </>
                    )}
                </ListToolbar>

                <FilterChips
                    chips={FILTERS.map((c) => ({ ...c, count: (filterGroups[c.key] || []).length }))}
                    value={activeFilter}
                    onChange={(key) => { setActiveFilter(key); setSelectedId(null); }}
                />

                {/* Att koppla: the one certain batch — these rows already
                    HAVE a booking; linking them is the whole job. One
                    sentence, one button, no dialog (link-only + per-row
                    Ångra = nothing to warn about). */}
                {activeFilter === FILTER_MATCHED && (filterGroups[FILTER_MATCHED] || []).length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151' }}>
                            Dessa rader har redan en bokföring — koppla ihop dem så är de klara.
                            Inget nytt bokförs och varje rad kan ångras.
                        </span>
                        <Button
                            variant="success"
                            size="sm"
                            style={{ marginLeft: 'auto' }}
                            onClick={handleLinkAll}
                            disabled={linkAllBusy}
                        >
                            {linkAllBusy ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Kopplar… {linkProgress ? `${linkProgress.done}/${linkProgress.total}` : ''}
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="me-1" />
                                    Koppla alla {(filterGroups[FILTER_MATCHED] || []).length}
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {/* Previous-years exit (quiet): rows the matcher can never
                    find because their bokföring lives in a system whose SIE
                    was never imported. Batch undo in the toast. */}
                {activeFilter === FILTER_ATTENTION && oldOpenLines.length > 0 && (
                    <div style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>
                            {oldOpenLines.length} rader är från tidigare år — redan bokförda i ett
                            annat system?
                        </span>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            style={{ fontSize: 13 }}
                            onClick={handleDismissOld}
                            disabled={dismissOldBusy}
                        >
                            {dismissOldBusy ? 'Markerar…' : 'Markera dem som avstämda'}
                        </Button>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-0">
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* HYBRID (owner decision 2026-07-26): the inspector sits
                    BESIDE the list — nothing overlaps the full-width
                    elements above — and list + inspector move as ONE
                    animation: the grid columns transition while the
                    inspector slides in (.bank-split / .bank-inspector). */}
                <div className={`bank-split${selectedLine ? ' open' : ''}`}>
                    <div>
                        {isLoading ? (
                            <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar transaktioner…</span>
                            </div>
                        ) : visibleLines.length === 0 ? (
                            statements.length === 0 ? (
                                /* Cold start — nothing imported yet */
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                    <Landmark size={32} style={{ opacity: 0.4 }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>
                                        Importera dina banktransaktioner
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 12 }}>
                                        Ladda upp din banks kontoutdrag (CSV) så matchar och
                                        föreslår LedgerLoop bokföringen automatiskt — och lär
                                        sig av varje rad du bokför.
                                    </p>
                                    <Button variant="primary" size="sm" onClick={() => setShowImportModal(true)}>
                                        <Upload size={14} className="me-1" />
                                        Importera transaktioner
                                    </Button>
                                </div>
                            ) : activeFilter === FILTER_ATTENTION && !searchText ? (
                                /* The goal state — positive, not a blank table */
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                    <CheckCircle size={32} style={{ color: '#10b981' }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600, color: '#111827' }}>
                                        {(filterGroups[FILTER_MATCHED] || []).length > 0
                                            ? 'Inget behöver din bedömning'
                                            : 'Allt är hanterat'}
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 0, color: '#6b7280' }}>
                                        {bookedCount > 0
                                            ? `${bookedCount} rader är bokförda eller avstämda. Importera fler transaktioner när du vill.`
                                            : 'Importera fler transaktioner när du vill.'}
                                    </p>
                                    {(filterGroups[FILTER_MATCHED] || []).length > 0 && (
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            style={{ marginTop: 12 }}
                                            onClick={() => { setActiveFilter(FILTER_MATCHED); setSelectedId(null); }}
                                        >
                                            {(filterGroups[FILTER_MATCHED] || []).length} rader väntar under Att koppla
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <EmptyState
                                    variant="no-matches"
                                    query={debouncedSearch}
                                    title={debouncedSearch ? 'Inga träffar' : 'Inga transaktioner i denna vy'}
                                    description="Byt filter för att se andra transaktioner."
                                />
                            )
                        ) : (
                            <div className="matching-list-box">
                                {windowedLines.map((line) => (
                                    <div
                                        key={line.id}
                                        style={{
                                            transition: 'opacity 300ms ease, transform 300ms ease',
                                            opacity: leavingIds.has(line.id) ? 0 : 1,
                                            transform: leavingIds.has(line.id) ? 'translateX(16px)' : 'none',
                                        }}
                                    >
                                        <StatementRow
                                            line={line}
                                            bankLeg={bankLegFor(line.account_number) || roleBankLeg}
                                            isSelected={line.id === selectedId}
                                            isPending={line.id === pendingId || line.id === processingId}
                                            disablePreview={Boolean(selectedLine || pendingId) || linkAllBusy}
                                            onSelect={() => handleSelectRow(line)}
                                        />
                                    </div>
                                ))}
                                {/* Windowed rendering: 50 at a time; the Alla
                                    view chains into server paging once the
                                    loaded window is exhausted. */}
                                {visibleLines.length > windowedLines.length ? (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                                            Visa fler (visar {windowedLines.length} av {visibleLines.length})
                                        </Button>
                                    </div>
                                ) : activeFilter === FILTER_ALL && totalIsServer && totalCount > statements.length && (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
                                            {loadingMore ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Laddar…
                                                </>
                                            ) : `Ladda fler (${statements.length} av ${totalCount})`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="bank-split-detail">
                    {selectedLine && (
                        <StatementDetail
                            line={selectedLine}
                            bankLeg={bankLegFor(selectedLine.account_number) || roleBankLeg}
                            companyId={companyId}
                            tier={selectedPaneMode}
                            onClose={() => setSelectedId(null)}
                            suggestions={suggestions.filter(
                                (s) => !selectedLine || !rejectedKeys.has(rejectionKey(selectedLine.id, s))
                            )}
                            suggestionsLoading={suggestionsLoading}
                            suggestionsError={suggestionsError}
                            actionBusy={actionBusy}
                            onConfirmSuggestion={handleConfirmSuggestion}
                            onBookTransfer={handleBookTransfer}
                            onRejectSuggestion={handleRejectSuggestion}
                            onRestoreRejections={handleRestoreRejections}
                            rejectedCount={rejectedCount}
                            onPrefillHistoryBooking={handlePrefillHistoryBooking}
                            onOpenNeighbor={handleOpenNeighbor}
                            prior={prior}
                            llm={llmState}
                            onAskAi={handleAskAi}
                            onUseLlmSuggestion={handleUseLlmSuggestion}
                            onCreateVerification={() => {
                                setVerificationPrefillRows(null);
                                setShowCreateVerificationModal(true);
                            }}
                            onMarkReconciledExternal={handleMarkReconciledExternal}
                            onConfirmSingle={handleConfirmSingle}
                            linkedVerification={linkedVerification}
                            onReset={handleReset}
                            onShowVerification={handleShowVerification}
                            onAskAssistant={hasAiAssistant ? handleAskAssistant : undefined}
                        />
                    )}
                    </div>
                </div>
            </div>

            {/* Import modal — evolved preset-driven flow (audit E1/E2/E3). */}
            <BankImportModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
            />

            {/* N-row booking wizard, optionally prefilled with transfer legs. */}
            <CreateVerificationFromTransactionModal
                show={showCreateVerificationModal}
                onHide={() => {
                    setShowCreateVerificationModal(false);
                    setVerificationPrefillRows(null);
                }}
                transaction={selectedLine}
                onSuccess={handleVerificationSuccess}
                initialRows={verificationPrefillRows}
                bankLeg={(selectedLine ? bankLegFor(selectedLine.account_number) : null) || roleBankLeg}
            />

            {/* Shared verification viewer (read-only from this surface). */}
            <VerificationDetailsModal
                showVerificationDetails={showVerificationDetails}
                setShowVerificationDetails={setShowVerificationDetails}
                selectedRow={verificationRow}
                viewOnly
            />

            {/* Avvisa-förslag reason dialog (replaces window.prompt) */}
            <Modal show={!!rejectDialog} onHide={() => !actionBusy && setRejectDialog(null)} centered>
                <Modal.Header closeButton={!actionBusy}>
                    <Modal.Title as="h6">Avvisa förslag</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Varför passar förslaget inte? Svaret hjälper systemet att lära sig — men det är valfritt.
                    </p>
                    <textarea
                        className="form-control"
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="t.ex. fel konto, fel motpart, beloppet stämmer inte …"
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setRejectDialog(null)} disabled={actionBusy}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={submitReject} disabled={actionBusy}>
                        {actionBusy ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Avvisar…
                            </>
                        ) : 'Avvisa förslaget'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Card/box/spinner styles come from the promoted design-system
                partial (scss/components/_modern-card.scss, Phase E). */}
        </>
    );
}

// Shared with the Skattekonto page (2026-07-27 rebuild) — same
// visual language, one source of truth.
export { Eyebrow, KonteringSlip, formatCurrency, formatDate };
